/**
 * xScan web — i18n dictionary (ES + EN).
 *
 * Per ADR-FRAMEWORK-028 v1.1 (in baseline) — full EN/ES localization
 * across all xTrust landings.
 *
 * Locale persisted in localStorage as 'xscan_locale' (default: 'es').
 * Language switcher button in nav toggles between locales.
 */

export const LOCALES = ['es', 'en'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'es';
export const STORAGE_KEY = 'xscan_locale';

export type DictEntry = { es: string; en: string };
export type Dict = Record<string, DictEntry>;

export const DICT: Dict = {
  // ─── Nav ─────────────────────────────────────────────────
  'nav.dashboard': { es: 'Mi Dashboard', en: 'My Dashboard' },
  'nav.login': { es: 'Iniciar Sesión', en: 'Sign In' },
  'nav.register': { es: 'Registrarse Gratis', en: 'Sign Up Free' },
  'nav.lang.switchToEN': { es: 'EN', en: 'EN' },
  'nav.lang.switchToES': { es: 'ES', en: 'ES' },

  // ─── Hero ────────────────────────────────────────────────
  'hero.badge': {
    es: '34 Módulos • Arquitectura Híbrida • AI Autonomous',
    en: '34 Modules • Hybrid Architecture • AI Autonomous',
  },
  'hero.title.line1': { es: 'Pentesting', en: 'Pentesting' },
  'hero.title.gradient': { es: 'Automatizado', en: 'Automated' },
  'hero.title.line2': { es: 'con Evidencia Real', en: 'with Real Evidence' },
  'hero.subtitle': {
    es: 'No solo detectamos vulnerabilidades — las explotamos de forma segura y entregamos la evidencia. Informe ejecutivo con CVSS v4, compliance mapping, madurez NIST CSF 2.0 y cuantificación de riesgo financiero.',
    en: 'We don\'t just detect vulnerabilities — we exploit them safely and deliver the evidence. Executive report with CVSS v4, compliance mapping, NIST CSF 2.0 maturity, and financial risk quantification.',
  },
  'hero.input.placeholder': {
    es: 'ejemplo.com o 203.0.113.1 (solo IPs públicas)',
    en: 'example.com or 203.0.113.1 (public IPs only)',
  },
  'hero.cta.scan': { es: 'Escanear', en: 'Scan' },
  'hero.disclaimer': {
    es: 'Regístrese con su correo corporativo · Verificación segura por OTP · Solo IPs públicas',
    en: 'Sign up with your corporate email · Secure OTP verification · Public IPs only',
  },
  'hero.stat.modules': { es: 'Módulos de Pentesting', en: 'Pentesting Modules' },
  'hero.stat.frameworks': { es: 'Frameworks Evaluados', en: 'Frameworks Assessed' },
  'hero.stat.levels': { es: 'Niveles de Validación', en: 'Validation Levels' },

  // ─── Features Grid ───────────────────────────────────────
  'features.eyebrow': { es: 'Módulos de Análisis', en: 'Analysis Modules' },
  'features.title': { es: 'Análisis Integral de Seguridad', en: 'Comprehensive Security Analysis' },
  'features.subtitle': {
    es: 'Nuestros módulos cubren los aspectos más críticos de la seguridad web y de infraestructura',
    en: 'Our modules cover the most critical aspects of web and infrastructure security',
  },
  'feature.headers.title': { es: 'Headers HTTP', en: 'HTTP Headers' },
  'feature.headers.desc': { es: 'Analiza headers de seguridad del servidor web', en: 'Analyzes web server security headers' },
  'feature.ssl.title': { es: 'SSL/TLS', en: 'SSL/TLS' },
  'feature.ssl.desc': { es: 'Verifica certificados y configuración de cifrado', en: 'Verifies certificates and encryption configuration' },
  'feature.dns.title': { es: 'DNS & Email', en: 'DNS & Email' },
  'feature.dns.desc': { es: 'Revisa SPF, DKIM, DMARC y configuración DNS', en: 'Reviews SPF, DKIM, DMARC and DNS configuration' },
  'feature.tech.title': { es: 'Tecnologías', en: 'Technologies' },
  'feature.tech.desc': { es: 'Detecta CMS, frameworks y versiones vulnerables', en: 'Detects CMS, frameworks and vulnerable versions' },
  'feature.ports.title': { es: 'Puertos', en: 'Ports' },
  'feature.ports.desc': { es: 'Escaneo de puertos abiertos y servicios expuestos', en: 'Open port scanning and exposed service detection' },
  'feature.web.title': { es: 'Vulnerabilidades Web', en: 'Web Vulnerabilities' },
  'feature.web.desc': { es: 'Archivos expuestos, misconfigurations y OWASP Top 10', en: 'Exposed files, misconfigurations, and OWASP Top 10' },
  'feature.subdomain.title': { es: 'Subdominios', en: 'Subdomains' },
  'feature.subdomain.desc': { es: 'Descubrimiento via Certificate Transparency y DNS', en: 'Discovery via Certificate Transparency and DNS' },
  'feature.evidence.title': { es: 'Evidence Engine v3.0', en: 'Evidence Engine v3.0' },
  'feature.evidence.desc': { es: 'Proof-of-exploitation con OOB callbacks (interactsh)', en: 'Proof-of-exploitation with OOB callbacks (interactsh)' },

  // ─── How It Works ────────────────────────────────────────
  'how.eyebrow': { es: 'Proceso Simple', en: 'Simple Process' },
  'how.title': { es: 'Cómo Funciona', en: 'How It Works' },
  'how.step.label': { es: 'PASO', en: 'STEP' },
  'how.step1.title': { es: 'Regístrese', en: 'Sign Up' },
  'how.step1.desc': { es: 'Ingrese su empresa, móvil y correo. Verificación rápida por OTP.', en: 'Enter your company, phone and email. Quick OTP verification.' },
  'how.step2.title': { es: 'Escanee', en: 'Scan' },
  'how.step2.desc': { es: 'Ingrese su URL o IP y seleccione los tipos de análisis.', en: 'Enter your URL or IP and select the analysis types.' },
  'how.step3.title': { es: 'Reciba su Informe', en: 'Get Your Report' },
  'how.step3.desc': { es: 'Reporte con hallazgos priorizados y plan de remediación.', en: 'Report with prioritized findings and remediation plan.' },

  // ─── What You Get ────────────────────────────────────────
  'whatuget.eyebrow': { es: 'Más que un Scanner', en: 'More than a Scanner' },
  'whatuget.title': { es: 'Qué Obtiene con cada Escaneo', en: 'What You Get with Every Scan' },
  'whatuget.subtitle': {
    es: 'Cada análisis genera evidencia accionable para su equipo técnico, su junta directiva, sus auditores y su aseguradora.',
    en: 'Every scan generates actionable evidence for your technical team, board, auditors, and insurer.',
  },
  'card1.title': { es: 'Evidencia de Explotabilidad', en: 'Proof of Exploitability' },
  'card1.desc': {
    es: 'No solo detectamos vulnerabilidades — demostramos que son explotables con evidencia técnica reproducible. Su equipo sabe exactamente qué arreglar y por qué.',
    en: 'We don\'t just detect vulnerabilities — we prove they are exploitable with reproducible technical evidence. Your team knows exactly what to fix and why.',
  },
  'card1.bullet1': { es: 'Request/response completo de cada prueba', en: 'Complete request/response for each test' },
  'card1.bullet2': { es: 'Scripts de reproducción (curl/Python)', en: 'Reproduction scripts (curl/Python)' },
  'card1.bullet3': { es: 'CVSS v4.0 + probabilidad de explotación (EPSS)', en: 'CVSS v4.0 + exploitation probability (EPSS)' },

  'card2.title': { es: 'Madurez NIST CSF 2.0', en: 'NIST CSF 2.0 Maturity' },
  'card2.desc': {
    es: 'Evaluación automática contra el framework de ciberseguridad más reconocido. 6 funciones, Tier 1-4, con mapeo a PCI-DSS, ISO 27001, OWASP y Ley 1581.',
    en: 'Automatic assessment against the most recognized cybersecurity framework. 6 functions, Tier 1-4, mapped to PCI-DSS, ISO 27001, OWASP, and data protection laws.',
  },
  'card2.bullet1': { es: '30 módulos mapeados a 6 funciones NIST', en: '30 modules mapped to 6 NIST functions' },
  'card2.bullet2': { es: 'Brechas identificadas con plan de cierre', en: 'Gaps identified with remediation plan' },
  'card2.bullet3': { es: '5 frameworks de cumplimiento evaluados', en: '5 compliance frameworks assessed' },

  'card3.title': { es: 'Riesgo en Dólares', en: 'Dollar-Quantified Risk' },
  'card3.desc': {
    es: 'Traducimos cada vulnerabilidad a impacto financiero. Su junta directiva entiende el riesgo en términos de negocio, no en jerga técnica.',
    en: 'We translate every vulnerability into financial impact. Your board understands risk in business terms, not technical jargon.',
  },
  'card3.bullet1': { es: 'Probabilidad de brecha a 12 meses', en: '12-month breach probability' },
  'card3.bullet2': { es: 'Pérdida anual esperada + costo estimado', en: 'Expected annual loss + estimated cost' },
  'card3.bullet3': { es: 'ROI de remediación vs no hacer nada', en: 'ROI of remediation vs. doing nothing' },

  'uc.tech.title': { es: 'Para su Equipo Técnico', en: 'For Your Technical Team' },
  'uc.tech.desc': {
    es: 'Hallazgos priorizados con evidencia y scripts de reproducción para remediar rápido.',
    en: 'Prioritized findings with evidence and reproduction scripts for fast remediation.',
  },
  'uc.board.title': { es: 'Para su Junta Directiva', en: 'For Your Board' },
  'uc.board.desc': {
    es: 'Score de madurez, riesgo en dólares y comparación con la industria — lenguaje de negocio.',
    en: 'Maturity score, dollar-quantified risk, and industry benchmarking — business language.',
  },
  'uc.insurer.title': { es: 'Para su Aseguradora', en: 'For Your Insurance Carrier' },
  'uc.insurer.desc': {
    es: 'Certificado de evaluación con madurez NIST, CRQ y compliance — lo que necesitan para cotizar.',
    en: 'Assessment certificate with NIST maturity, CRQ, and compliance — what they need to quote.',
  },
  'uc.auditor.title': { es: 'Para sus Auditores', en: 'For Your Auditors' },
  'uc.auditor.desc': {
    es: 'Mapeo a PCI-DSS 4.0, ISO 27001:2022, SOC 2, NIST 800-53 y Ley 1581 por hallazgo.',
    en: 'Mapping to PCI-DSS 4.0, ISO 27001:2022, SOC 2, NIST 800-53, and data protection laws per finding.',
  },

  // ─── Tools ───────────────────────────────────────────────
  'tools.eyebrow': { es: 'Tecnología Abierta', en: 'Open Technology' },
  'tools.title': {
    es: 'Potenciado por las Mejores Herramientas Open Source',
    en: 'Powered by the Best Open Source Tools',
  },
  'tools.note': {
    es: 'xScan integra estas herramientas pero el código de la plataforma es proprietary, source-available — ver LICENSE.md',
    en: 'xScan integrates these tools but the platform code is proprietary, source-available — see LICENSE.md',
  },

  // ─── CTA ─────────────────────────────────────────────────
  'cta.title': { es: '¿Listo para proteger su empresa?', en: 'Ready to protect your company?' },
  'cta.subtitle': {
    es: 'Miles de empresas en Latinoamérica confían en xScan para diagnosticar y mejorar su postura de seguridad. Comience hoy.',
    en: 'Thousands of companies across Latin America trust xScan to diagnose and improve their security posture. Start today.',
  },
  'cta.start': { es: 'Comenzar Gratis', en: 'Start Free' },
  'cta.knowxclos': { es: 'Conocer XCloud Solutions', en: 'About XCloud Solutions' },
  'cta.remediation': { es: '¿Necesita servicios de remediación o seguridad gestionada?', en: 'Need remediation or managed security services?' },
  'cta.contactus': { es: 'Contáctenos', en: 'Contact us' },

  // ─── xTrust banner ───────────────────────────────────────
  'xtrust.partof': { es: 'Parte de', en: 'Part of' },
  'xtrust.suite': { es: 'Suite Cybersecurity + IA Soberana', en: 'Cybersecurity + Sovereign AI Suite' },
};

export function t(key: string, locale: Locale): string {
  const entry = DICT[key];
  if (!entry) {
    if (typeof console !== 'undefined') console.warn(`[i18n] missing key: ${key}`);
    return `[?${key}]`;
  }
  return entry[locale] ?? entry.es ?? `[?${key}]`;
}

/**
 * Read locale from localStorage (browser only). Falls back to default.
 */
export function readStoredLocale(): Locale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'es' || stored === 'en') return stored;
  } catch {
    // localStorage may be unavailable (private mode, etc.)
  }
  return DEFAULT_LOCALE;
}

/**
 * Persist locale to localStorage. No-op on server.
 */
export function writeStoredLocale(locale: Locale): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // ignore storage failures
  }
}
